import { useState } from "react";
import axios from "axios";
import { Reveal } from "../components/Reveal";
import { SampleTag } from "../components/SampleTag";
import { SITE } from "../data/sampleData";

const PRINCIPLES = [
    {
        title: "Realistic, not flattering",
        text: "Formal seating, profile-based questions, follow-ups and silence — the conditions of the real room.",
    },
    {
        title: "Scored, not vague",
        text: "Five marks out of ten and a written remark. You leave knowing exactly what to fix.",
    },
    {
        title: "Calm, not theatrical",
        text: "Boards run the way good boards do: quietly, fairly and strictly on time.",
    },
];

const EMPTY_MSG = { name: "", email: "", message: "" };

const AboutContact = () => {
    const [msg, setMsg] = useState(EMPTY_MSG);
    const [sent, setSent] = useState(null);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState("");

    const set = (key) => (e) => setMsg((m) => ({ ...m, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSendError("");
        setSending(true);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
                msg,
            );
            setSent({ ...msg, triage: data.triage });
            setMsg(EMPTY_MSG);
        } catch (err) {
            setSendError("Could not send right now — please try again shortly.");
        } finally {
            setSending(false);
        }
    };

    return (
        <main className="py-20 md:py-28" data-testid="about-page">
            <div className="wrap">
                {/* ---------- About ---------- */}
                <Reveal className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="eyebrow">About</p>
                        <h2 className="mt-4 max-w-2xl text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            Practice, in the <em className="italic text-brass">right room.</em>
                        </h2>
                    </div>
                    <SampleTag />
                </Reveal>

                <Reveal delay={0.05}>
                    <p className="mt-10 max-w-prose text-base leading-relaxed text-ink-muted sm:text-lg">
                        The Panel Room is a mock interview platform for aspirants of
                        the Civil Services (UPSC), Banking and State PSC
                        examinations. Our boards are seated the way real boards are:
                        a chairperson, subject experts and a psychologist — retired
                        civil servants, senior bankers and practising psychologists
                        among them. One mock board, thirty minutes, and a scored
                        report within a day.
                    </p>
                    <p className="mt-5 max-w-prose text-base leading-relaxed text-ink-muted">
                        This demonstration site runs entirely on sample content, so
                        every name, question and score here is a placeholder waiting
                        for the real thing.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-10 sm:grid-cols-3 sm:gap-8">
                    {PRINCIPLES.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.08}>
                            <div className="border-t border-brass/60 pt-6" data-testid={`principle-${i + 1}`}>
                                <h3 className="text-xl tracking-tight">{p.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                                    {p.text}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                {/* ---------- Contact ---------- */}
                <div className="mt-24 border-t border-line pt-16 md:mt-32" data-testid="contact-section">
                    <Reveal>
                        <p className="eyebrow">Contact</p>
                        <h2 className="mt-4 text-3xl leading-tight tracking-tight sm:text-4xl">
                            Write to the board.
                        </h2>
                    </Reveal>

                    <div className="mt-10 grid gap-12 lg:grid-cols-2 lg:gap-10">
                        <div className="grid gap-10 sm:grid-cols-1 sm:gap-8">
                            {[
                                { label: "Email", value: SITE.contact.email },
                                { label: "WhatsApp", value: SITE.contact.whatsapp },
                                { label: "Hours", value: SITE.contact.hours },
                            ].map((item, i) => (
                                <Reveal key={item.label} delay={i * 0.08}>
                                    <div data-testid={`contact-${item.label.toLowerCase()}`}>
                                        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink-muted">
                                            {item.label}
                                        </p>
                                        <p className="mt-3 text-base text-ink">{item.value}</p>
                                    </div>
                                </Reveal>
                            ))}
                            <Reveal delay={0.2}>
                                <p className="flex items-center gap-3 text-xs text-ink-muted">
                                    <SampleTag /> The WhatsApp number is real; email
                                    and hours are still sample content.{" "}
                                    {SITE.contact.response}
                                </p>
                            </Reveal>
                        </div>

                        <Reveal delay={0.1}>
                            {sent ? (
                                <div
                                    data-testid="contact-confirmation"
                                    className="border border-brass bg-brass-soft p-8"
                                >
                                    <h3 className="font-display text-2xl tracking-tight">
                                        Message received.
                                    </h3>
                                    <p className="mt-4 text-sm leading-relaxed text-ink">
                                        Thanks, {sent.name}. The owner will reply to{" "}
                                        {sent.email} within one working day.
                                    </p>
                                    {sent.triage?.ai_classified && (
                                        <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.18em] text-brass">
                                            Filed as {sent.triage.category} · urgency{" "}
                                            {sent.triage.urgency}
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        data-testid="contact-reset-btn"
                                        onClick={() => setSent(null)}
                                        className="btn-ghost mt-8"
                                    >
                                        Send another
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    data-testid="contact-form"
                                    className="border border-line bg-surface p-8"
                                >
                                    <div className="mb-6">
                                        <label htmlFor="contact-name" className="field-label">
                                            Name
                                        </label>
                                        <input
                                            id="contact-name"
                                            type="text"
                                            required
                                            minLength={2}
                                            value={msg.name}
                                            onChange={set("name")}
                                            data-testid="contact-name-input"
                                            className="field"
                                            placeholder="Your name"
                                        />
                                    </div>
                                    <div className="mb-6">
                                        <label htmlFor="contact-email" className="field-label">
                                            Email
                                        </label>
                                        <input
                                            id="contact-email"
                                            type="email"
                                            required
                                            value={msg.email}
                                            onChange={set("email")}
                                            data-testid="contact-email-input"
                                            className="field"
                                            placeholder="you@example.com"
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label htmlFor="contact-message" className="field-label">
                                            Message
                                        </label>
                                        <textarea
                                            id="contact-message"
                                            required
                                            minLength={5}
                                            maxLength={2000}
                                            rows={4}
                                            value={msg.message}
                                            onChange={set("message")}
                                            data-testid="contact-message-input"
                                            className="field resize-none border border-line px-3"
                                            placeholder="Ask about boards, panels or scheduling…"
                                        />
                                    </div>
                                    {sendError && (
                                        <p className="mt-4 text-sm text-red-700 dark:text-red-400" data-testid="contact-error">
                                            {sendError}
                                        </p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="contact-submit-btn"
                                        className="btn-primary mt-8 w-full disabled:opacity-60"
                                    >
                                        {sending ? "Sending…" : "Send message"}
                                    </button>
                                </form>
                            )}
                        </Reveal>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default AboutContact;
